/**
 * @file 角色管理模块 - 常量定义
 * @description 定义角色管理相关的常量和标签映射
 * 
 * @module types/role/constants
 * @category Role - 角色管理
 * 
 * @author YSFL Team
 * @created 2025-10-18
 * @last-updated 2025-10-18
 * 
 * ==========================================
 * 文件职责
 * ==========================================
 * 1. 为枚举提供中文标签映射
 * 2. 定义业务常量
 * 3. 提供下拉选项数组
 * 
 * ==========================================
 * 对应后端文件
 * ==========================================
 * - Model: backend/app/Models/Role.php
 *   - 静态属性和方法中的标签映射
 *   - getDataScopeTextAttribute() 方法中的文本映射
 * 
 * ==========================================
 * 注意事项
 * ==========================================
 * 1. ⚠️ 标签文本必须与后端完全一致
 * 2. ⚠️ 枚举值必须与 enums.ts 中定义的值匹配
 * 3. ⚠️ OPTIONS 数组用于下拉选择组件
 * 4. ⚠️ 修改枚举时必须同步更新对应的 LABELS
 * 
 * ==========================================
 * 更新时机
 * ==========================================
 * 当以下情况发生时需要更新本文件：
 * - 新增枚举类型
 * - 修改枚举值的中文标签
 * - 新增业务常量
 * 
 * ==========================================
 * 验证方式
 * ==========================================
 * 1. 对照后端 Model 中的文本映射
 * 2. 确认所有枚举值都有对应的标签
 * 3. 运行 `npm run type-check` 确保编译通过
 */

import { DataScope } from './enums';

/**
 * 数据权限范围标签映射
 * 
 * @description 将数据权限范围枚举值映射为中文标签
 * @backend Role::getDataScopeTextAttribute()
 */
export const DATA_SCOPE_LABELS: Record<DataScope, string> = {
  [DataScope.ALL]: '全部数据',
  [DataScope.CUSTOM]: '自定义数据',
  [DataScope.DEPT]: '本部门数据',
  [DataScope.DEPT_AND_CHILD]: '本部门及以下数据',
  [DataScope.SELF]: '仅本人数据',
};

/**
 * 数据权限范围选项
 * 
 * @description 用于下拉选择组件的选项数组
 */
export const DATA_SCOPE_OPTIONS = [
  { label: '全部数据', value: DataScope.ALL },
  { label: '自定义数据', value: DataScope.CUSTOM },
  { label: '本部门数据', value: DataScope.DEPT },
  { label: '本部门及以下数据', value: DataScope.DEPT_AND_CHILD },
  { label: '仅本人数据', value: DataScope.SELF },
];

/**
 * 状态标签映射
 */
export const STATUS_LABELS: Record<number, string> = {
  0: '禁用',
  1: '启用',
};

/**
 * 状态选项
 */
export const STATUS_OPTIONS = [
  { label: '禁用', value: 0 },
  { label: '启用', value: 1 },
];

/**
 * 是否系统角色标签映射
 */
export const IS_SYSTEM_LABELS: Record<number, string> = {
  0: '否',
  1: '是',
};

/**
 * 是否系统角色选项
 */
export const IS_SYSTEM_OPTIONS = [
  { label: '否', value: 0 },
  { label: '是', value: 1 },
];

/**
 * 默认排序值
 */
export const DEFAULT_SORT_ORDER = 0;

/**
 * 默认守卫名称
 */
export const DEFAULT_GUARD_NAME = 'web';

